import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Input, Space, Table, message } from "antd";
import { ExpandAltOutlined, SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import Highlighter from "react-highlight-words";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Stats from "../tester-teamlead/tester-stats";
import "./paymentstatsV2.css";

const apiUrl =
  "https://u2xdo4pej8.execute-api.us-east-1.amazonaws.com/dev/payment-stats-for-super-admin";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PaymentStatsV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedTester, setSelectedTester] = useState(null);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewClick = (record) => {
    setSelectedTester(record);
    setOpen(true);
  };

  const calculatePayment = (count) => {
    const payment = (count || 0) * 0.01;
    return payment.toFixed(3);
  };

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      if (data && data.length > 0) {
        const testersWithPayment = data.map((tester) => ({
          ...tester,
          payment: calculatePayment(tester.last_month_valid_count),
        }));
        setTesters(testersWithPayment);
      } else {
        setTesters([]);
      }
    } catch (error) {
      console.error("Error fetching payment stats:", error);
      messageApi.error("Failed to fetch payment statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    const downloadData = sortedUsers.map((item) => ({
      First_Name: item.first_name,
      Last_Name: item.last_name,
      Last_Month_Valid_Count: item.last_month_valid_count,
      Payment: item.payment,
      Account_Title: item.account_title,
      Account_Number: item.account_number,
      Bank_Name: item.bank_name,
    }));
    const csvData = Papa.unparse(downloadData);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "Payment-And-Valid-OTP-Last-Month.csv");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sortedUsers = useMemo(() => {
    return [...testers].sort(
      (a, b) => (b.last_month_valid_count || 0) - (a.last_month_valid_count || 0)
    );
  }, [testers]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="paymentstats-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="paymentstats-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "paymentstats-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex]?.toString() || "").toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Tester Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "14%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Valid Tests Received Last Month",
      dataIndex: "last_month_valid_count",
      key: "last_month_valid_count",
      width: "12%",
      ...getColumnSearchProps("last_month_valid_count"),
      sorter: (a, b) => (a.last_month_valid_count || 0) - (b.last_month_valid_count || 0),
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "descend",
    },
    {
      title: "Payment (USD)",
      dataIndex: "payment",
      key: "payment",
      width: "10%",
      sorter: (a, b) => parseFloat(a.payment || 0) - parseFloat(b.payment || 0),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Account Title",
      dataIndex: "account_title",
      key: "account_title",
      width: "14%",
      ...getColumnSearchProps("account_title"),
      sorter: (a, b) => (a.account_title || "").localeCompare(b.account_title || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Account Number",
      dataIndex: "account_number",
      key: "account_number",
      width: "12%",
      ...getColumnSearchProps("account_number"),
      sorter: (a, b) => (a.account_number || "").localeCompare(b.account_number || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Bank Name",
      dataIndex: "bank_name",
      key: "bank_name",
      width: "14%",
      ...getColumnSearchProps("bank_name"),
      sorter: (a, b) => (a.bank_name || "").localeCompare(b.bank_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "14%",
      render: (_, record) => (
        <Button
          type="default"
          icon={<ExpandAltOutlined />}
          onClick={() => handleViewClick(record)}
          className="paymentstats-v2-btn-view"
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div className="paymentstats-v2">
      {contextHolder}
      <div className="paymentstats-v2-header">
        <h1 className="paymentstats-v2-title">Last Month Payout Summary</h1>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCsv}
          className="paymentstats-v2-download-btn"
        >
          Download CSV
        </Button>
      </div>

      <div className="paymentstats-v2-card">
        <Table
          columns={columns}
          dataSource={sortedUsers}
          rowKey={(r, idx) => r.id || `${r.first_name || ""}-${r.last_name || ""}-${idx}`}
          loading={loading}
          locale={{ emptyText: loading ? "Loading..." : "No data" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="paymentstats-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        className="paymentstats-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AppBar className="paymentstats-v2-appbar" position="relative">
          <Toolbar className="paymentstats-v2-toolbar">
            <Typography variant="h6" component="div" className="paymentstats-v2-dialog-title">
              Tester Statistics Details
            </Typography>
            {selectedTester && (
              <Typography variant="body2" className="paymentstats-v2-tester-name">
                {selectedTester.first_name} {selectedTester.last_name}
              </Typography>
            )}
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div className="paymentstats-v2-body">
          {selectedTester && <Stats id={selectedTester.id} />}
        </div>
      </Dialog>
    </div>
  );
};

export default PaymentStatsV2;
